import { AxiosError } from 'axios'
import { ApiBase } from './ApiBase'
import IResult, { IResultAttachment } from './types/result'
import {
  PendingKind, PendingResult, queuePendingResult, SendOutcome, trackSaving
} from '../offlineMode/pendingResults'
import { store } from '../store'

// the request reached no server (offline, connection dropped, timeout): the answer must be kept
export const ANSWER_SAVE_TIMEOUT_MS = 20000

function isTransportError(error: unknown): boolean {
  return !!error && !(error as AxiosError).response
}

function resultUrl(checklistId: number, resultSetId: number, id?: unknown, accessToken?: string | null) {
  let url = `/checklists/${checklistId}/result_sets/${resultSetId}/results${id ? `/${id}` : ''}`
  if (accessToken) url += `?access_token=${accessToken}`
  return url
}

export default class ResultApi extends ApiBase {
  public async create(
    checklistId: number,
    resultSetId: number,
    data: IResult,
    accessToken?: string | null
  ) {
    if (this.isOffline()) {
      const checklist = await this.offlineDb.checklists.get(checklistId)
      const local = await this.offlineDb.resultSets.get(resultSetId)
      // the process is not on the device (the app went offline while it was open): keep the answer
      if (!checklist || !local) return this.keepForLater('create', checklistId, resultSetId, data, accessToken)
      const question = checklist.areas.flatMap((x) => x.questions)
        .find((x) => x.id === data.question_id)
      const result: IResult = {
        ...data,
        result_set_id: resultSetId,
        question,
        question_id: question.id,
        action: [],
        _dirty: true,
        cloud_attachments: [],
        attachments: []
      }
      return this.offlineDb.addResult(result)
    }
    let url = `/checklists/${checklistId}/result_sets/${resultSetId}/results`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    delete data.cloud_attachments
    let response
    try {
      response = await trackSaving(this.post<IResult>(
        url,
        data
      ))
    } catch (error) {
      if (isTransportError(error)) return this.keepForLater('create', checklistId, resultSetId, data, accessToken)
      throw error
    }
    // ApiBase returns nothing on a network error
    if (!response) return this.keepForLater('create', checklistId, resultSetId, data, accessToken)

    return response.data
  }

  /**
   * Updates an existing result
   *
   * @param checklistId
   * @param resultSetId
   * @param data
   * @param accessToken
   */
  public async update(
    checklistId: number,
    resultSetId: number,
    data: IResult,
    accessToken?: string | null
  ) {
    if (this.isOffline()) {
      const local = await this.offlineDb.resultSets.get(resultSetId)
      // the process is not on the device (the app went offline while it was open): keep the answer
      if (!local) return this.keepForLater('update', checklistId, resultSetId, data, accessToken)
      return this.offlineDb.updateResult(data)
    }
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cloud_attachments, attachments, comments, versions, question, ...rest
    } = data

    let url = `/checklists/${checklistId}/result_sets/${resultSetId}/results/${
      data.id
    }`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    delete data.cloud_attachments

    let response
    try {
      // an answer waits at most ANSWER_SAVE_TIMEOUT_MS, then it is kept and sent again (same id, so no duplicate):
      // the checklist must not stay locked for the 2 minutes of other requests when a response gets lost
      response = await trackSaving(this.put<IResult>(url, rest, { timeout: ANSWER_SAVE_TIMEOUT_MS }))
    } catch (error) {
      if (isTransportError(error)) return this.keepForLater('update', checklistId, resultSetId, data, accessToken)
      throw error
    }
    // ApiBase returns nothing on a network error
    if (!response) return this.keepForLater('update', checklistId, resultSetId, data, accessToken)

    return response.data
  }

  // Keeps an answer that could not be sent (see offlineMode/pendingResults) and returns it as if
  // saved locally, so the checklist keeps working; the notice shows that it is still pending.
  private keepForLater(
    kind: PendingKind,
    checklistId: number,
    resultSetId: number,
    data: IResult,
    accessToken?: string | null
  ): IResult {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cloud_attachments, attachments, comments, versions, question, action, _pending, ...payload
    } = data as IResult & { _pending?: boolean }
    queuePendingResult({
      kind: kind === 'update' && data.id ? 'update' : 'create',
      checklistId,
      resultSetId,
      accessToken: accessToken ?? null,
      userId: store.getState().core.user?.id ?? null,
      payload: { ...payload, result_set_id: resultSetId }
    })
    return {
      ...data, result_set_id: resultSetId, action: [], _pending: true
    } as IResult
  }

  // sends one queued answer directly (also while the app shows offline mode)
  public async sendPending(entry: PendingResult): Promise<SendOutcome> {
    const url = resultUrl(entry.checklistId, entry.resultSetId, entry.kind === 'update' ? entry.payload.id : null, entry.accessToken)
    try {
      if (entry.kind === 'update') await this.http.put(url, entry.payload, { timeout: 20000 })
      else await this.http.post(url, entry.payload, { timeout: 20000 })
      return 'ok'
    } catch (error) {
      const status = (error as AxiosError).response?.status
      // no response, or the session must be renewed first: try again later
      if (!status || status === 401) return 'network'
      return 'rejected'
    }
  }

  public async sendRuleEmails(
    checklistId: number,
    resultSetId: number,
    resultId: string,
    accessToken?: string | null
  ) {
    let url = `/checklists/${checklistId}/result_sets/${resultSetId}/results/${resultId}/execute_mail_actions`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    return await this.post<void>(url)
  }

  public deleteResult(
    checklistId: number,
    resultSetId: number,
    data: IResult,
    accessToken?: string | null
  ) {
    let url = `/checklists/${checklistId}/result_sets/${resultSetId}/results/${
      data.id
    }`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    return this.delete(url)
  }

  public async addAttachment(
    checklistId: number,
    resultSetId: number,
    questionId: number,
    file: File,
    areaPosition: number,
    accessToken?: string | null
  ) {
    const formData = new FormData()
    formData.append('filepond', file)
    formData.append('area_position', areaPosition.toString())

    formData.append('access_token', areaPosition.toString())

    let url = `/checklists/${checklistId}/result_sets/${resultSetId}/attachment?question_id=${questionId}`

    if (accessToken) {
      url += `&access_token=${accessToken}`
    }

    const response = await this.post<IResult>(url, formData)
    return response.data
  }

  public updateAttachment(
    resultSetId: number,
    resultId: string,
    id: number,
    data: string,
    accessToken?: string | null
  ) {
    let url = `/attachments/${id}`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    const params = {
      upload: data
    }

    return this.put<IResultAttachment>(url, params)
  }

  public async deleteAttachment(
    resultSetId: number,
    resultId: string,
    id: number,
    accessToken?: string | null
  ): Promise < void > {
    if (this.isOffline()) {
      await this.offlineDb.deleteAttachment(resultSetId, resultId, id)
      return
    }
    let url = `/attachments/${id}`

    if (accessToken) {
      url += `?access_token=${accessToken}`
    }

    await super.delete(url)
  }
}
